// Page layout (02-building-blocks.md, "Page layout"; sizes from 01-design.md): the side list's
// header, the page's header (a plain bar with Back on a phone), content and the splash.
import { useSyncExternalStore, type ReactNode } from 'react';
import { PRODUCT } from '../core/product';
import { IconButton } from './Button';
import { icons } from './icons';
import { t } from '../i18n';

export type Screen = 'phone' | 'tablet' | 'desktop';

const phone = matchMedia('(max-width: 750px)');
const tablet = matchMedia('(max-width: 1124px)');
const screenNow = (): Screen => (phone.matches ? 'phone' : tablet.matches ? 'tablet' : 'desktop');
const subscribe = (l: () => void) => {
  phone.addEventListener('change', l);
  tablet.addEventListener('change', l);
  return () => {
    phone.removeEventListener('change', l);
    tablet.removeEventListener('change', l);
  };
};

/** Phone up to 750 px, tablet 751–1124 px, desktop above; measured live. */
export const useScreen = () => useSyncExternalStore(subscribe, screenNow);

export const SideListHeader = ({ title, actions }: { title: ReactNode; actions?: ReactNode }) => (
  <header className="side-header">
    <h2 className="side-title">{title}</h2>
    {actions}
  </header>
);

export interface PageHeaderProps {
  icon?: ReactNode;
  title: ReactNode;
  actions?: ReactNode;
  /** On a phone, in the icon's place: the back arrow (07). */
  lead?: ReactNode;
  /** The title isn't the page's heading (the lobby: its large name is, 08). */
  plain?: boolean;
}

/** Back on a phone's page: every page opened from a tab (07, v1.58.31). */
export const BackButton = ({ onClick, ...rest }: { onClick: () => void; 'aria-disabled'?: boolean; 'data-desyr-page-close'?: string }) => <IconButton label={t('common.back')} icon={icons.arrowBack} tooltip={false} onClick={onClick} {...rest} />;

export function PageHeader({ icon, title, actions, lead, plain }: PageHeaderProps) {
  const isPhone = useScreen() === 'phone';
  return (
    <header className={isPhone && !lead ? 'page-header centred' : 'page-header'}>
      {isPhone ? lead : icon}
      {plain ? <span className="page-title">{title}</span> : <h1 className="page-title">{title}</h1>}
      {actions && <div className="page-actions">{actions}</div>}
    </header>
  );
}

export const Content = ({ children }: { children: ReactNode }) => <div className="content">{children}</div>;

/** The start screens' page in desyr.chat's look: the glow, the content, the wordmark at the bottom. */
export function Splash({ children, ...hooks }: { children?: ReactNode } & Record<`data-${string}`, string>) {
  return (
    <div className="splash si" {...hooks}>
      <div className="splash-middle">{children}</div>
      <p className="splash-name wordmark">{PRODUCT}</p>
    </div>
  );
}
